import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import api from '../utils/api';
import useAuthStore from '../store/useAuthStore';
import { AuroraBlobs, GrainOverlay, GlassChip, MagneticButton, Reveal, fadeUp, stagger } from '../components/ui';
import { FiPackage, FiUser, FiMapPin, FiLogOut } from 'react-icons/fi';

const TIER_TINT = {
  '—': { bg: 'rgba(154,142,134,0.12)', color: 'var(--muted)' },
  'Saheli': { bg: 'rgba(122,31,43,0.12)', color: 'var(--primary)' },
  'Sakhi': { bg: 'rgba(176,138,62,0.14)', color: 'var(--gold)' },
};

export default function AccountPage() {
  const { user, logout, updateProfile } = useAuthStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState('profile');
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);

  const { data: ordersData } = useQuery({
    queryKey: ['my-orders'],
    queryFn: () => api.get('/orders/my'),
    enabled: tab === 'orders',
  });

  const handleSaveProfile = async () => {
    setSaving(true);
    try { await updateProfile({ name, email }); toast.success('Profile updated!'); }
    catch (err) { toast.error(err.message || 'Failed to update'); }
    setSaving(false);
  };

  const tier = user?.tier || '—';
  const tint = TIER_TINT[tier] || TIER_TINT['—'];
  const initial = user?.name?.[0] || user?.phone?.[3] || 'V';

  return (
    <div>
      {/* Hero strip */}
      <section style={{ position: 'relative', background: '#1A100E', overflow: 'hidden', padding: '56px 24px 48px' }}>
        <AuroraBlobs />
        <GrainOverlay />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1000, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 24 }}>
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} style={{
            width: 88, height: 88, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--primary), var(--gold))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 36, color: '#FFF', fontFamily: 'var(--display)', fontWeight: 600,
            boxShadow: '0 16px 40px rgba(0,0,0,0.3), 0 0 0 4px rgba(255,255,255,0.08)',
            flexShrink: 0,
          }}>
            {initial}
          </motion.div>
          <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.15 }}>
            <GlassChip>Member since {new Date(user?.createdAt || Date.now()).getFullYear()}</GlassChip>
            <h1 className="serif" style={{ fontSize: 'clamp(28px, 4vw, 44px)', color: '#FFF', lineHeight: 1.1, marginTop: 14, letterSpacing: '-0.02em' }}>{user?.name || 'Hello!'}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
              <span style={{ fontSize: 13, color: 'rgba(237,227,209,0.7)' }}>{user?.phone}</span>
              {tier !== '—' && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 10px', borderRadius: 999,
                  background: tint.bg, color: tint.color,
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
                }}>
                  ★ {tier}
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '36px 24px 64px' }}>
        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 32, borderBottom: '1px solid var(--line-2)' }}>
          {[
            { id: 'profile', label: 'Profile', icon: <FiUser size={14} /> },
            { id: 'orders', label: 'Orders', icon: <FiPackage size={14} /> },
            { id: 'addresses', label: 'Addresses', icon: <FiMapPin size={14} /> },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`ve-tab ${tab === t.id ? 'active' : ''}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {tab === 'profile' && (
            <motion.div key="profile" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}
              className="ve-glass-form-card" style={{ padding: 28, maxWidth: 520 }}>
              <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <motion.div variants={fadeUp}>
                  <label className="fld-lab">Full Name</label>
                  <input className="ve-fld" value={name} onChange={e => setName(e.target.value)} placeholder="Priya Sharma" />
                </motion.div>
                <motion.div variants={fadeUp}>
                  <label className="fld-lab">Email</label>
                  <input className="ve-fld" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="priya@example.com" />
                </motion.div>
                <motion.div variants={fadeUp}>
                  <label className="fld-lab">Mobile</label>
                  <input className="ve-fld" value={user?.phone || ''} disabled />
                </motion.div>
              </motion.div>
              <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
                <MagneticButton variant="primary" onClick={handleSaveProfile} disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </MagneticButton>
                <MagneticButton variant="ghost" onClick={() => { logout(); navigate('/'); toast.success('Signed out'); }}>
                  <FiLogOut size={14} /> Sign out
                </MagneticButton>
              </div>
            </motion.div>
          )}

          {tab === 'orders' && (
            <motion.div key="orders" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}>
              {!ordersData?.orders?.length ? (
                <EmptyHint title="No orders yet" body="Start exploring our handcrafted collection." cta={{ to: '/', label: 'Browse shop' }} />
              ) : (
                <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {ordersData.orders.map(order => (
                    <motion.div key={order._id} variants={fadeUp} whileHover={{ y: -3 }}>
                      <Link to={`/orders/${order._id}/track`} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                        <div style={{
                          background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(229,219,200,0.6)', borderRadius: 16,
                          backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                          padding: 20, display: 'flex', alignItems: 'center', gap: 16,
                        }}>
                          <span style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--gold-soft)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FiPackage size={18} />
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: '0.04em' }}>{order.orderId}</div>
                            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
                              {order.items?.length} item{order.items?.length > 1 ? 's' : ''} · {new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <span className="serif" style={{ fontSize: 18, fontWeight: 600 }}>₹{order.pricing?.total?.toLocaleString('en-IN')}</span>
                            <StatusPill status={order.status} />
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}

          {tab === 'addresses' && (
            <motion.div key="addresses" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.25 }}>
              {(!user?.addresses || user.addresses.length === 0) ? (
                <EmptyHint title="No saved addresses" body="Add one at checkout — it'll be saved here for next time." />
              ) : (
                <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
                  {user.addresses.map(addr => (
                    <motion.div key={addr._id} variants={fadeUp} style={{
                      background: 'rgba(255,255,255,0.6)', border: '1px solid rgba(229,219,200,0.6)', borderRadius: 16,
                      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
                      padding: 18,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{addr.name}</div>
                        {addr.isDefault && <span style={{ fontSize: 9, fontWeight: 700, padding: '3px 8px', background: 'var(--primary-soft)', color: 'var(--primary)', borderRadius: 999, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Default</span>}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.7 }}>
                        {addr.line1}{addr.line2 ? `, ${addr.line2}` : ''}<br />
                        {addr.city}, {addr.state} — {addr.pincode}<br />
                        <span style={{ color: 'var(--muted)' }}>{addr.phone}</span>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function EmptyHint({ title, body, cta }) {
  return (
    <Reveal style={{ textAlign: 'center', padding: '60px 0', maxWidth: 380, margin: '0 auto' }}>
      <h3 className="serif" style={{ fontSize: 24, marginBottom: 10 }}>{title}</h3>
      <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: cta ? 20 : 0, lineHeight: 1.7 }}>{body}</p>
      {cta && <MagneticButton to={cta.to} variant="primary">{cta.label}</MagneticButton>}
    </Reveal>
  );
}

function StatusPill({ status }) {
  const map = {
    Delivered: { bg: '#E5EFE0', color: '#3F6240' }, Shipped: { bg: '#DDE7F0', color: '#34557A' },
    Confirmed: { bg: '#DDE7F0', color: '#34557A' }, Pending: { bg: '#FBEED6', color: '#8A6020' },
    Returned: { bg: '#F1DBD8', color: '#8B3F36' }, Cancelled: { bg: '#F1DBD8', color: '#8B3F36' },
  };
  const style = map[status] || map.Pending;
  return <span style={{ ...style, padding: '5px 12px', borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{status}</span>;
}
